/**
 * featureUsageStore.ts
 *
 * Counts how signed-in users use beta features. Events are summed per Vietnam
 * date in localStorage (one queue per username, so a shared browser never
 * sends one user's counts under another's login) and sent in batches by
 * plugins/featureUsage.ts.
 */
import { defineStore } from 'pinia'
import { useClients } from '@/services/clients'
import { useAuthStore } from '@/stores/authStore'
import { vietnamToday } from '@/utils/date'
import { type UsageEntry, type UsageQueue, addUsage, queueEntries, subtractEntries } from '@/utils/featureUsageQueue'

/** localStorage key prefix; the username follows. */
export const FEATURE_USAGE_STORAGE_PREFIX = 'featureUsage:'

/** Entries per request; the server refuses more than 100. */
const MAX_ENTRIES_PER_SEND = 100

export const useFeatureUsageStore = defineStore('featureUsage', () => {
  const { featureUsageClient } = useClients()
  const authStore = useAuthStore()

  let sending: Promise<void> | null = null

  /** Records one event for the signed-in user. Admins are not tracked. */
  function track(feature: string, event: string): void {
    const username = authStore.username
    if (!username || authStore.isAdmin) return
    const queue = readQueue(username)
    if (queue === null) return
    writeQueue(username, addUsage(queue, feature, event, vietnamToday()))
  }

  /** Sends the signed-in user's queue. Only one send runs at a time. */
  function flush(): Promise<void> {
    sending ??= send().finally(() => {
      sending = null
    })
    return sending
  }

  async function send(): Promise<void> {
    const username = authStore.username
    if (!username) return
    const queue = readQueue(username)
    if (queue === null) return
    const entries = queueEntries(queue, MAX_ENTRIES_PER_SEND)
    if (entries.length === 0) return

    try {
      await featureUsageClient.recordFeatureUsage({ usages: entries.map(toUsage) })
    } catch (error) {
      console.warn('Failed to send feature usage; will retry:', error)
      return
    }

    // Re-read: events tracked during the request must survive.
    const latest = readQueue(username)
    if (latest !== null) writeQueue(username, subtractEntries(latest, entries))
  }

  return { track, flush }
})

// =============================================================================
// Helper Functions
// =============================================================================

/** The user's queue, {} when absent or corrupt, null when storage is unusable. */
function readQueue(username: string): UsageQueue | null {
  let raw: string | null
  try {
    raw = localStorage.getItem(FEATURE_USAGE_STORAGE_PREFIX + username)
  } catch {
    return null
  }
  if (raw === null) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as UsageQueue) : {}
  } catch {
    return {} // a corrupt value is replaced by the next write
  }
}

/** Saves the queue, removing the key when it is empty. Storage errors are ignored. */
function writeQueue(username: string, queue: UsageQueue): void {
  try {
    const key = FEATURE_USAGE_STORAGE_PREFIX + username
    if (Object.keys(queue).length === 0) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(queue))
  } catch {
    // Private mode or quota: usage is best effort.
  }
}

function toUsage(e: UsageEntry) {
  const [year, month, day] = e.date.split('-').map(Number)
  return { feature: e.feature, event: e.event, usageDate: { year, month, day }, count: e.count }
}
